import {Popover} from '@mintdata/mint-kit/impl/components/popover/Popover';
import {IPopupProps, PopupAnchor, SourceAnchor} from '@mintdata/mint-kit/impl/components/popover/PopupTypes';
import {IInputEditProps, InputEdit} from '@mintdata/mint-kit/impl/properties/components/inputEdit/InputEdit';
import {
    ArrowIconRenderer,
    IArrowIconRendererProps
} from '@mintdata/mint-kit/impl/properties/components/suggest/common/ArrowIconProps';
import {
    ClearIconRenderer,
    IClearIconRendererProps
} from '@mintdata/mint-kit/impl/properties/components/suggest/common/ClearIconProps';
import {IListItemsProps} from '@mintdata/mint-kit/impl/properties/components/suggest/common/ListItemsProps';
import {IQueryListRendererProps, QueryList} from '@mintdata/mint-kit/impl/properties/components/suggest/QueryList';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';

import * as Keys from '@mintdata/mint-kit/impl/util/Keys';
import * as classnames from 'classnames/bind';
import _isFunction from 'lodash/isFunction';
import * as React from 'react';

const styles = require('@mintdata/mint-kit/impl/properties/components/suggest/Suggest.less');
const cx = classnames.bind(styles);

export interface ISuggestProps<T> extends IListItemsProps<T>
{
    className?:string;
    style?:React.CSSProperties;

    inputWrapperClassName?:string;
    inputWrapperStyle?:React.CSSProperties;

    inputProps?:Partial<IInputEditProps>;
    popoverProps?:Partial<IPopupProps>;

    selectedItem:T;
    itemToInputValue?:(item:T) => string;
    inputValueToItem?:(inputValue:string) => T;

    closeOnSelect?:boolean;
    openOnInputFocus?:boolean;
    isEditable?:boolean;
    isSearchable?:boolean;
    isClearable?:boolean;

    arrowIconRenderer?:ArrowIconRenderer;
    clearIconRenderer?:ClearIconRenderer;

    onClearIconMouseDown?:(event:React.SyntheticEvent<HTMLElement>) => void;

    onActiveItemChange?:(item:T) => void;
}

export interface ISuggestState<T>
{
    activeItem?:T;
    query:string;
    isOpen:boolean;
    isTyping:boolean;
}

export class Suggest<T> extends React.Component<ISuggestProps<T>, ISuggestState<T>>
{
    public static defaultProps = {
        inputProps: {},
        popoverProps: {},
        closeOnSelect: true,
        openOnInputFocus: true,
        isEditable: true,
        isSearchable: true,
        isClearable: false
    };

    public static ofType<T>()
    {
        return Suggest as new (props:ISuggestProps<T>) => Suggest<T>;
    }

    public state:ISuggestState<T> = {
        query: '',
        isOpen: false,
        isTyping: false
    };

    private _popoverId:string = IdUtil.newGUID();
    private _popoverSource:HTMLElement | null;
    private _input:HTMLInputElement | null;
    private _queryList:QueryList<T> | null;
    private _observer:IntersectionObserver = null;

    private _refHandlers = {
        input: (ref:HTMLInputElement | null) =>
        {
            const {inputProps} = this.props;
            this._input = ref;

            if( _isFunction(inputProps.onRef) )
            {
                inputProps.onRef(ref);
            }
        },
        popoverSource: (ref:HTMLElement | null) => this._popoverSource = ref,
        queryList: (ref:QueryList<T> | null) => this._queryList = ref
    };

    public render()
    {
        // omit props specific to this component, spread the rest.
        const {
            inputProps,
            popoverProps,
            selectedItem,
            itemToInputValue,
            inputValueToItem,
            closeOnSelect,
            openOnInputFocus,
            isEditable,
            ...restProps
        } = this.props;

        const {activeItem, query} = this.state;
        const TypedQueryList = QueryList.ofType<T>();

        return (
            <TypedQueryList
                {...restProps}
                ref={this._refHandlers.queryList}
                activeItem={activeItem}
                query={query}
                renderer={this._renderQueryList}
                onActiveItemChange={this._handleActiveItemChange}
                onItemSelect={this._handleItemSelect}
                compareItems={this._onCompareItems}
            />
        );
    }

    private _onCompareItems = (item1:T, item2:T) =>
    {
        const {itemToInputValue} = this.props;

        return item1 === null && item2 === null
            || item1 && item2 && (itemToInputValue(item1) === itemToInputValue(item2));
    }

    private _onAnyScroll = () =>
    {
        if( this.state.isOpen )
            this.forceUpdate();
    }

    private _intersectionCallback = (entries:IntersectionObserverEntry[]) =>
    {
        if( !entries[0].isIntersecting )
            this.setState({isOpen: false});
    }

    public componentDidMount()
    {
        const {items, selectedItem, itemToInputValue = this._defaultItemToInputValue} = this.props;
        const {activeItem} = this.state;

        document.addEventListener('scroll', this._onAnyScroll, true);

        if( 'IntersectionObserver' in window )
        {
            var options = {
                root: null,
                rootMargin: '0px',
                threshold: 1.0
            }

            const popupSource:HTMLElement = this.props.inputProps.label ? this._input : this._popoverSource;

            this._observer = new IntersectionObserver(this._intersectionCallback, options);
            this._observer.observe(popupSource);
        }

        const selectedItemValue = itemToInputValue(selectedItem);
        const selectedItemIndex = items.findIndex(item => itemToInputValue(item) === selectedItemValue);

        if( activeItem )
        {
            const activeItemValue = activeItem ? itemToInputValue(this.state.activeItem) : '';

            if( activeItemValue !== selectedItemValue)
            {
                this.setState({activeItem: selectedItemIndex >= 0 ? selectedItem : items[0]});
                this._handleActiveItemChange(selectedItem);
            }
        }
        else
        {
            this.setState({activeItem: selectedItem});
        }

        this._queryList.scrollActiveItemIntoView();
    }

    public componentDidUpdate(prevProps:ISuggestProps<T>, prevState:ISuggestState<T>)
    {
        const {selectedItem, itemToInputValue = this._defaultItemToInputValue} = this.props;
        const {isOpen, activeItem} = this.state;

        if( isOpen && !prevState.isOpen && this._queryList != null )
        {
            const selectedItemValue = itemToInputValue(selectedItem);
            const activeItemValue = itemToInputValue(activeItem);

            if( activeItemValue !== selectedItemValue)
            {
                this.setState({activeItem: selectedItem});
            }
            
            this._handleActiveItemChange(selectedItem);
        }
    }

    public componentWillUnmount()
    {
        document.removeEventListener('scroll', this._onAnyScroll, true);
    }

    private _renderQueryList = (listProps:IQueryListRendererProps<T>) =>
    {
        const {
            className,
            inputWrapperClassName,
            inputWrapperStyle,
            style,
            selectedItem,
            inputProps,
            popoverProps,
            isEditable,
            isSearchable,
            isClearable,
            itemToInputValue = this._defaultItemToInputValue
        } = this.props;

        const {handleKeyDown, itemList} = listProps;
        const {query, isTyping, isOpen} = this.state;

        const popupSource = inputProps.label ? this._input : this._popoverSource;

        let inputValue:string = itemToInputValue(selectedItem);

        if( !isEditable && isTyping )
        {
            inputValue = query;
        }

        const {
            className: inputClassName,
            ...restInputProps
        } = inputProps;

        const {
            className: popoverClassName,
            ...restPopoverProps
        } = popoverProps;

        const selectValue = inputValue || inputProps.placeholder;
        const selectTextStyle:React.CSSProperties = inputValue ? null : inputProps.placeholderStyle;

        return (
            <div
                className={cx('base', className)}
                style={style}
            >
                <div
                    className={cx('inputWrapper', inputWrapperClassName)}
                    style={inputWrapperStyle}
                    ref={this._refHandlers.popoverSource}
                >
                    {
                        isSearchable ? (
                            <InputEdit
                                className={cx('input', inputClassName)}
                                value={inputValue}
                                {...restInputProps}
                                onChange={this._handleInputChange}
                                onFocus={this._handleInputFocus}
                                onKeyDown={this._getInputKeyDownHandler(handleKeyDown)}
                                onRef={this._refHandlers.input}
                            />
                        ) : (
                            <div
                                className={cx('select', inputProps.className)}
                                tabIndex={0}
                                onFocus={this._handleDivFocus}
                                onClick={this._handleDivClick}
                                onKeyDown={this._getInputKeyDownHandler(handleKeyDown) as any}
                                style={inputProps.style}
                            >
								<span
                                    className={cx('selectText')}
                                    style={selectTextStyle}
                                >
									{selectValue}
								</span>
                            </div>
                        )
                    }
                    {this._renderArrowIcon()}
                    {isClearable && this._renderClearIcon()}
                </div>
                {
                    isOpen &&
                    <Popover
                        className={cx('popover', popoverClassName)}
                        sourceAnchor={SourceAnchor.LeftBottom}
                        popupAnchor={PopupAnchor.LeftTop}
                        {...restPopoverProps}
                        id={this._popoverId}
                        popupSource={popupSource}
                        onDismissed={this._closePopover}
                    >
                        <div onKeyDown={handleKeyDown}>
                            {itemList}
                        </div>
                    </Popover>
                }
            </div>
        );
    };

    private _renderArrowIcon = ():JSX.Element =>
    {
        const {arrowIconRenderer} = this.props;

        const props:IArrowIconRendererProps = {
            onMouseDown: this._handleArrowMouseDown
        };

        if( _isFunction(arrowIconRenderer) )
        {
            return arrowIconRenderer(props);
        }

        return this._defaultArrowIconRenderer(props);
    };

    private _renderClearIcon = ():JSX.Element =>
    {
        const {clearIconRenderer} = this.props;

        const props:IClearIconRendererProps = {
            onMouseDown: this._handleClearMouseDown
        };

        if( _isFunction(clearIconRenderer) )
        {
            return clearIconRenderer(props);
        }

        return this._defaultClearIconRenderer(props);
    };

    private _defaultArrowIconRenderer = (props:IArrowIconRendererProps) =>
    {
        const {onMouseDown} = props;

        return (
            <div
                className={cx('arrowIconWrapper')}
                onMouseDown={onMouseDown}
            >
                <svg
                    width={'24'}
                    height={'24'}
                    viewBox={'0 0 24 24'}
                    className={cx('arrowIcon')}
                >
                    <path d={'M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z'} />
                    <path fill={'none'} d={'M0 0h24v24H0V0z'} />
                </svg>
            </div>
        );
    };

    private _defaultClearIconRenderer = (props:IClearIconRendererProps) =>
    {
        const {onMouseDown} = props;

        return (
            <div
                className={cx('clearIconWrapper')}
                onMouseDown={onMouseDown}
            >
                <svg
                    width={'24'}
                    height={'24'}
                    viewBox={'0 0 24 24'}
                    className={cx('clearIcon')}
                >
                    <path
                        d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                    <path d="M0 0h24v24H0z" fill="none" />
                </svg>
            </div>
        );
    };

    private _defaultItemToInputValue = (item:T) => item ? item.toString() : '';

    private _defaultInputValueToItem = (value:string) => value as any;

    private _handleActiveItemChange = (activeItem?:T) =>
    {
        this.setState({activeItem});

        const {onActiveItemChange} = this.props;
        
        if( onActiveItemChange )
            onActiveItemChange(activeItem);
    };

    private _handleItemSelect = (item:T, event:React.SyntheticEvent<HTMLElement>) =>
    {
        const {onItemSelect, closeOnSelect} = this.props;

        let nextOpenState:boolean;

        if( !closeOnSelect )
        {
            if( this._input != null )
            {
                this._input.focus();
            }

            this._selectText();
            nextOpenState = true;
        }
        else
        {
            nextOpenState = false;
        }

        this.setState({
            query: '',
            isOpen: nextOpenState,
            isTyping: false
        });

        onItemSelect(item, event);
    };

    private _handleInputChange = (event:React.FormEvent<HTMLInputElement>) =>
    {
        const {
            inputProps,
            onItemSelect,
            isEditable,
            inputValueToItem = this._defaultInputValueToItem
        } = this.props;

        const value = event.currentTarget.value;

        this.setState({
            query: value,
            isTyping: true
        });

        if( isEditable )
        {
            onItemSelect(inputValueToItem(value));
        }

        if( _isFunction(inputProps.onChange) )
        {
            inputProps.onChange(event);
        }
    };

    private _selectText = () =>
    {
        requestAnimationFrame(() =>
        {
            if( this._input != null )
            {
                this._input.setSelectionRange(0, this._input.value.length);
            }
        });
    };

    private _handleInputFocus = (event:React.FocusEvent<HTMLInputElement>) =>
    {
        const {openOnInputFocus, inputProps} = this.props;

        this._selectText();

        if( openOnInputFocus )
        {
            this._openPopover();
        }

        if( _isFunction(inputProps.onFocus) )
        {
            inputProps.onFocus(event);
        }
    };

    private _handleDivFocus = (event:React.FocusEvent<HTMLDivElement>) =>
    {
        const {openOnInputFocus} = this.props;

        if( openOnInputFocus )
        {
            this._openPopover();
        }
    };

    private _handleDivClick = (event:React.MouseEvent<HTMLDivElement>) =>
    {
        const {openOnInputFocus} = this.props;

        if( openOnInputFocus )
        {
            this._openPopover();
        }
    };

    private _getInputKeyDownHandler = (handleQueryListKeyDown:React.EventHandler<React.KeyboardEvent<HTMLElement>>) =>
    {
        return (event:React.KeyboardEvent<HTMLInputElement>) =>
        {
            const {isOpen} = this.state;
            const {which} = event;

            if(
                which !== Keys.ARROW_LEFT &&
                which !== Keys.ARROW_RIGHT
            )
            {
                this._openPopover();
            }

            if( which === Keys.TAB )
            {
                this._closePopover();
            }

            if( isOpen )
            {
                handleQueryListKeyDown(event);
            }
        };
    };

    private _handleArrowMouseDown = (event:React.SyntheticEvent<HTMLElement>) =>
    {
        this.setState((prevState) => ({
            isOpen: !prevState.isOpen
        }));
    };

    private _handleClearMouseDown = (event:React.SyntheticEvent<HTMLElement>) =>
    {
        const {onClearIconMouseDown} = this.props;

        this.setState({
            query: '',
            isOpen: false,
            isTyping: false
        });

        if( onClearIconMouseDown )
            onClearIconMouseDown(event);
    };

    private _openPopover = () =>
    {
        this.setState({isOpen: true});
    }

    private _closePopover = () =>
    {
        const {selectedItem, isEditable} = this.props;

        const state = {
            query: '',
            isOpen: false,
            isTyping: false
        } as ISuggestState<T>;

        if( !isEditable )
        {
            state.activeItem = selectedItem;
        }

        this.setState(state);
    };
}
